import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Smartphone, X, CheckCircle2, PackageX, AlertTriangle, CalendarDays } from "lucide-react";
import {
  resolverPrecoAparelho, parcelasAparelho,
  type FormaPagamentoAparelho, type GrupoPlanoAparelho, type MatrizPrecosAparelho,
} from "@/lib/aparelhosImport";
import type { LinhaComposicao, TipoLinha } from "@/constants/planosCNPJ";

/**
 * Seletor de APARELHO dentro da linha da proposta (par do Passaport).
 * Modal denso no padrão v2 do CRM: busca cmdk, filtro por fabricante,
 * forma 24×/12×/à vista e preço SEMPRE resolvido pela matriz modelo×plano
 * (Configurações ▸ Produtos ▸ Aparelhos). Sem plano na linha, não abre —
 * o preço do aparelho depende do plano.
 */

interface AparelhoCatalogoRow {
  codigo: string;
  nome: string;
  ativo: boolean;
  metadata: {
    fabricante?: string;
    gama?: string;
    competencia?: string;
    precos?: {
      base: number | null;
      total_compartilhado: number | null;
      hibrido: Record<string, number>;
      renovacao: Record<string, number>;
      total_individual: Record<string, number>;
    };
  };
}

interface EstoqueModelo { modelo_slug: string; quantidade: number; data_foto: string }

/**
 * Renovação usa as colunas de RENOVAÇÃO da Claro (S–W); todo o resto — venda
 * nova, portabilidade, incremento — usa as colunas M–R.
 *
 * ⚠️ A chave se chama HIBRIDO por acidente do importador, que le as colunas por
 * letra. "Híbrido" NAO e' familia de plano da Claro (conferido no material de
 * 29/07/2026); la' a unica "híbrida" e' TECNOLOGIA hibrida, de fixa/TV. Na tela
 * o consultor le "Venda nova". Nao troque a chave sem migrar o JSON de
 * planos_proposta_catalogo.metadata->precos->hibrido. (Douglas 09/08/2026)
 */
function grupoDaLinha(tipo: TipoLinha): GrupoPlanoAparelho {
  return tipo === "RENOVACAO" ? "RENOVACAO" : "HIBRIDO";
}

function matrizDe(row: AparelhoCatalogoRow): MatrizPrecosAparelho {
  const p = row.metadata?.precos;
  const numRec = (r?: Record<string, number>) =>
    Object.fromEntries(Object.entries(r ?? {}).map(([k, v]) => [Number(k), Number(v)]));
  return {
    base: p?.base ?? null,
    totalCompartilhado: p?.total_compartilhado ?? null,
    hibrido: numRec(p?.hibrido),
    renovacao: numRec(p?.renovacao),
    totalIndividual: numRec(p?.total_individual),
  };
}

const fmt = (v: number) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const FORMAS: { id: FormaPagamentoAparelho; label: string }[] = [
  { id: "24x", label: "24× na fatura" },
  { id: "12x", label: "12× na fatura" },
  { id: "avista", label: "À vista" },
];

interface Props {
  linha: LinhaComposicao;
  disabled?: boolean;
  onChange: (aparelho: LinhaComposicao["aparelho"] | undefined) => void;
  tipoPessoa?: "PF" | "PJ";
  /** dentro da fileira "Leva" do cartão da linha: sem rótulo, botão "+ Aparelho" */
  compacto?: boolean;
}

export function AparelhoSelectorLinha({ linha, disabled, onChange, tipoPessoa = "PJ", compacto = false }: Props) {
  const [open, setOpen] = useState(false);
  const [forma, setForma] = useState<FormaPagamentoAparelho>("24x");
  const [fabricante, setFabricante] = useState<string | null>(null);

  const temPlano = !!linha.planoId && !!linha.plano;
  const grupo = grupoDaLinha(linha.tipo);
  const gb = linha.plano?.gb ?? null;

  const { data: catalogo = [] } = useQuery({
    queryKey: ["aparelhos-catalogo-proposta", tipoPessoa],
    staleTime: 5 * 60_000,
    enabled: open || !!linha.aparelho,
    queryFn: async (): Promise<AparelhoCatalogoRow[]> => {
      const { data, error } = await supabase
        .from("planos_proposta_catalogo" as never)
        .select("codigo, nome, ativo, metadata")
        .eq("categoria", "aparelhos")
        .eq("tipo_pessoa", tipoPessoa)
        .eq("ativo", true)
        .order("nome");
      if (error) return [];
      return (data || []) as unknown as AparelhoCatalogoRow[];
    },
  });

  const { data: estoque = [] } = useQuery({
    queryKey: ["aparelhos-estoque-modelo"],
    staleTime: 60_000,
    enabled: open,
    queryFn: async (): Promise<EstoqueModelo[]> => {
      const { data, error } = await supabase
        .from("v_aparelhos_estoque_modelo" as never)
        .select("modelo_slug, quantidade, data_foto");
      if (error) return [];
      return (data || []) as unknown as EstoqueModelo[];
    },
  });
  const estoqueMap = useMemo(() => new Map(estoque.map((e) => [e.modelo_slug, e.quantidade])), [estoque]);
  const dataFoto = estoque[0]?.data_foto ?? null;

  const fabricantes = useMemo(() => {
    const cont = new Map<string, number>();
    for (const r of catalogo) {
      const f = String(r.metadata?.fabricante ?? "").toUpperCase();
      if (f) cont.set(f, (cont.get(f) ?? 0) + 1);
    }
    return [...cont.entries()].sort((a, b) => b[1] - a[1]);
  }, [catalogo]);

  const lista = useMemo(() => {
    return catalogo
      .map((row) => ({
        row,
        resolvido: resolverPrecoAparelho(matrizDe(row), grupo, gb, forma),
        qtd: estoqueMap.get(row.codigo) ?? 0,
      }))
      .filter(({ row, resolvido }) =>
        resolvido != null &&
        (!fabricante || String(row.metadata?.fabricante ?? "").toUpperCase() === fabricante))
      .sort((a, b) => (b.qtd - a.qtd) || a.row.nome.localeCompare(b.row.nome));
  }, [catalogo, grupo, gb, forma, estoqueMap, fabricante]);

  const selecionar = (row: AparelhoCatalogoRow) => {
    const r = resolverPrecoAparelho(matrizDe(row), grupo, gb, forma);
    if (!r) return;
    onChange({
      slug: row.codigo,
      nome: row.nome,
      forma,
      preco: r.preco,
      parcela: parcelasAparelho(r.preco, forma)?.valor,
      grupoUsado: r.grupoUsado,
      faixaGb: r.faixaGb,
    });
    setOpen(false);
  };

  const sel = linha.aparelho;
  const contextoPlano = temPlano
    ? `${grupo === "RENOVACAO" ? "Renovação" : "Venda nova"}${gb != null ? ` · plano ${gb}GB` : ""}`
    : null;

  return (
    <div className={compacto ? "inline-flex items-center min-w-0" : "flex items-center gap-2 min-w-0"}>
      {!compacto && <span className="text-xs text-muted-foreground whitespace-nowrap">📱 Aparelho:</span>}

      {sel ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className={compacto
                ? "inline-flex min-w-0 items-center gap-1 rounded-full bg-[#F3E9EA] px-2.5 py-0.5 text-[11px] font-semibold text-[#7A2235] dark:bg-[#2A1A1D] dark:text-[#C87484]"
                : "text-xs px-2 py-0.5 rounded-full border bg-success/10 border-success text-success font-medium inline-flex items-center gap-1 min-w-0"}>
                <span className="truncate">
                  {sel.nome} · {sel.forma === "avista" ? fmt(sel.preco) : `${sel.forma === "24x" ? "24×" : "12×"} ${fmt(sel.parcela ?? sel.preco / (sel.forma === "24x" ? 24 : 12))}`}
                </span>
                {!disabled && (
                  <button type="button" onClick={() => onChange(undefined)} className="shrink-0 hover:text-destructive" aria-label="Remover aparelho">
                    <X className="w-3 h-3" />
                  </button>
                )}
              </span>
            </TooltipTrigger>
            <TooltipContent className="text-xs max-w-[280px]">
              <p className="font-medium">{sel.nome}</p>
              <p>Total {fmt(sel.preco)} · {sel.forma === "avista" ? "à vista no cartão (preço base, sem subsídio)" : `${sel.forma === "24x" ? "24×" : "12×"} de ${fmt(sel.parcela ?? 0)} na fatura Claro`}</p>
              <p className="text-muted-foreground mt-1">
                Faixa {sel.grupoUsado}{sel.faixaGb ? ` ${sel.faixaGb}GB` : ""} — o preço muda se trocar o plano da linha.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : temPlano ? (
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen(true)}
          className={compacto
            ? "rounded-full border border-dashed border-border px-2.5 py-0.5 text-[11px] text-muted-foreground transition-colors hover:border-primary hover:text-foreground disabled:opacity-50"
            : cn(
              "text-xs px-2 py-0.5 rounded-full border transition-colors",
              "bg-muted/50 border-border text-muted-foreground hover:border-primary hover:text-foreground",
            )}
        >
          {compacto ? "+ Aparelho" : "+ adicionar"}
        </button>
      ) : (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-xs px-2 py-0.5 rounded-full border border-dashed text-muted-foreground/60 cursor-not-allowed select-none">
                escolha o plano primeiro
              </span>
            </TooltipTrigger>
            <TooltipContent className="text-xs">O preço do aparelho depende do plano desta linha.</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-4 pt-4 pb-3 border-b bg-muted/30 space-y-0">
            <DialogTitle className="flex items-center gap-2 text-base">
              <span className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <Smartphone className="h-4 w-4" />
              </span>
              Escolher aparelho
              {contextoPlano && (
                <Badge variant="outline" className="ml-1 font-normal text-xs">{contextoPlano}</Badge>
              )}
            </DialogTitle>
            <div className="flex flex-wrap items-center gap-1.5 pt-2">
              {FORMAS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setForma(f.id)}
                  className={cn(
                    "text-xs px-2.5 py-1 rounded-full border transition-colors",
                    forma === f.id
                      ? "bg-primary text-primary-foreground border-primary font-medium"
                      : "bg-card text-muted-foreground hover:text-foreground",
                  )}
                >
                  {f.label}
                </button>
              ))}
              {forma === "avista" && (
                <span className="inline-flex items-center gap-1 text-[11px] text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                  <AlertTriangle className="h-3 w-3" /> preço base — sem o subsídio do plano
                </span>
              )}
            </div>
          </DialogHeader>

          <Command className="rounded-none">
            <CommandInput placeholder="Buscar modelo ou fabricante…" autoFocus />
            {fabricantes.length > 1 && (
              <div className="flex flex-wrap items-center gap-1 px-3 py-2 border-b bg-card">
                <button
                  type="button"
                  onClick={() => setFabricante(null)}
                  className={cn(
                    "text-[11px] px-2 py-0.5 rounded-full border transition-colors",
                    fabricante === null ? "bg-foreground text-background border-foreground" : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  Todas
                </button>
                {fabricantes.map(([f, n]) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setFabricante(fabricante === f ? null : f)}
                    className={cn(
                      "text-[11px] px-2 py-0.5 rounded-full border transition-colors",
                      fabricante === f ? "bg-foreground text-background border-foreground" : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {f} <span className="opacity-60">{n}</span>
                  </button>
                ))}
              </div>
            )}
            <CommandList className="max-h-[46vh] overflow-y-auto overscroll-contain">
              <CommandEmpty>
                Nenhum aparelho encontrado.
                {catalogo.length === 0 && " Importe a tabela do mês em Configurações ▸ Produtos ▸ Aparelhos."}
              </CommandEmpty>
              {lista.map(({ row, resolvido, qtd }) => {
                const parcela = forma === "avista" ? null : parcelasAparelho(resolvido!.preco, forma);
                return (
                  <CommandItem
                    key={row.codigo}
                    value={`${row.nome} ${row.metadata?.fabricante ?? ""}`}
                    onSelect={() => selecionar(row)}
                    className="px-3 py-2 cursor-pointer border-b last:border-0 rounded-none data-[selected=true]:bg-muted/60"
                  >
                    <div className="flex items-center justify-between gap-3 w-full min-w-0">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{row.nome}</p>
                        <p className="text-[11px] text-muted-foreground truncate">
                          {String(row.metadata?.fabricante ?? "")} · {String(row.metadata?.gama ?? "").replace("SMARTPHONE ", "")}
                          {resolvido!.faixaGb ? ` · faixa ${resolvido!.faixaGb}GB` : resolvido!.grupoUsado === "TOTAL_COMPARTILHADO" ? " · total compartilhado" : ""}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-semibold tabular-nums">
                          {parcela ? <>{parcela.n}× {fmt(parcela.valor)}</> : fmt(resolvido!.preco)}
                        </p>
                        <p className="text-[11px] text-muted-foreground tabular-nums">
                          {parcela ? `total ${fmt(resolvido!.preco)}` : "à vista no cartão"}
                        </p>
                      </div>
                      {qtd > 0 ? (
                        <Badge variant="outline" className="shrink-0 gap-1 text-emerald-700 border-emerald-300 bg-emerald-50 text-[10px] px-1.5">
                          <CheckCircle2 className="w-3 h-3" /> {qtd} un
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="shrink-0 gap-1 text-muted-foreground text-[10px] px-1.5">
                          <PackageX className="w-3 h-3" /> s/ estoque
                        </Badge>
                      )}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandList>
          </Command>

          <div className="px-4 py-2.5 border-t bg-muted/30 flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <Smartphone className="h-3 w-3" />
              Preço resolvido pelo plano desta linha · parcelas na fatura Claro
            </span>
            {dataFoto && (
              <span className="inline-flex items-center gap-1">
                <CalendarDays className="h-3 w-3" />
                estoque de {dataFoto.split("-").reverse().join("/")}
              </span>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
